'use client';

// imports
import { Brain, Zap, Eye, Leaf, Sparkles } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

// constants
const features = [
  {
    icon: Brain,
    title: 'AI-Powered Analysis',
    description: 'Identifies tongue biomarkers linked to diabetes risk using a trained deep learning model. Designed for preliminary screening, not clinical diagnosis.',
    color: 'blue',
    bg: 'bg-blue-50',
    iconColor: 'text-blue-500',
    border: 'border-blue-100',
    hoverBorder: 'hover:border-blue-200',
    badge: 'Deep Learning',
    badgeColor: 'bg-blue-50 text-blue-600 border-blue-100',
  },
  {
    icon: Zap,
    title: 'Instant Results',
    description: 'Upload your image and receive a probabilistic diabetes risk estimate within seconds for early awareness.',
    color: 'amber',
    bg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    border: 'border-amber-100',
    hoverBorder: 'hover:border-amber-200',
    badge: '< 30 seconds',
    badgeColor: 'bg-amber-50 text-amber-600 border-amber-100',
  },
  {
    icon: Eye,
    title: 'Explainable AI',
    description: 'Highlights key tongue features that influenced the prediction, supporting transparency as an assistive screening tool.',
    color: 'purple',
    bg: 'bg-purple-50',
    iconColor: 'text-purple-500',
    border: 'border-purple-100',
    hoverBorder: 'hover:border-purple-200',
    badge: 'Transparent',
    badgeColor: 'bg-purple-50 text-purple-600 border-purple-100',
  },
  {
    icon: Leaf,
    title: 'Non-Invasive',
    description: 'Uses a simple tongue image to offer accessible, non-invasive initial insights, complementing, not replacing, lab-based assessments.',
    color: 'emerald',
    bg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
    border: 'border-emerald-100',
    hoverBorder: 'hover:border-emerald-200',
    badge: 'Pain-free',
    badgeColor: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  },
];

const base = 'transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]';
const hidden = 'opacity-0 translate-y-8';
const show = 'opacity-100 translate-y-0';

// component
export default function FeaturesSection() {

  // hooks
  const { ref, isVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white">
      
      {/* container */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16">
        
        {/* header */}
        <div className="text-center mb-14">
          <div className={`${base} ${isVisible ? show : hidden} inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-600 rounded-full px-4 py-1.5 text-xs font-semibold mb-4`}>
            <Sparkles size={14} className="text-blue-500" />
            What Makes Insighta Different
          </div>
          <h2 className={`${base} delay-150 ${isVisible ? show : hidden} text-3xl md:text-4xl font-bold text-slate-900 mb-4`}>
            Diabetes Risk Screening That{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Works For You
            </span>
          </h2>
          <p className={`${base} delay-300 ${isVisible ? show : hidden} text-slate-500 text-base max-w-3xl mx-auto leading-relaxed`}>
            Insighta AI combines cutting-edge AI with traditional tongue diagnosis principles to deliver accessible and trustworthy health insights.
          </p>
        </div>

        {/* grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          
          {features?.map((feature, index) => {
            const Icon = feature?.icon;
            
            return (
              <div key={feature?.title} className="relative h-full">
                    
                    {/* animation */}
                    <div
                      className={`
                        h-full
                        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                        transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
                      `}
                      style={{
                        transitionDelay: `${400 + index * 120}ms`,
                      }}
                    >
                      {/*card*/}
                      <div
                        className={`group relative bg-white rounded-2xl p-6 border ${feature?.border} ${feature?.hoverBorder} shadow-soft hover:shadow-medium hover:-translate-y-1 cursor-default transition-transform transition-shadow duration-300 h-full`}
                      >
                        {/* icon */}
                        <div className={`w-12 h-12 ${feature?.bg} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon size={22} className={feature?.iconColor} />
                        </div>

                        {/* badge */}
                        <span className={`inline-block text-xs font-semibold border rounded-full px-2.5 py-0.5 mb-3 ${feature?.badgeColor}`}>
                          {feature?.badge}
                        </span>

                        {/* content */}
                        <h3 className="text-slate-800 font-bold text-base mb-3 leading-snug">
                          {feature?.title}
                        </h3>

                        <p className="text-slate-500 text-sm leading-relaxed">
                          {feature?.description}
                        </p>

                        {/* deco */}
                        <div className={`absolute top-0 right-0 w-20 h-20 ${feature?.bg} rounded-bl-[3rem] rounded-tr-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-300`} />
                      </div>
                    </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}